"use client"

import { cn } from "@/lib/utils"

/** One vertical gauge segment: `h` is its share (%) of the bar height. */
export type MarqueeGaugeSegment = { h: number; color: string; active?: boolean }

/** A result card in the auto-scrolling biomarker marquee (copied from the
 *  /report "See your health in color, not chaos" section). Set `muted` for the
 *  greyed "not measured" card (e.g. Lp(a) on a standard panel). */
export type MarqueeBiomarkerCard = {
  label: string
  value: string
  unit: string
  status: string
  /** Tailwind bg + text classes for the status pill (ignored when muted). */
  statusBg: string
  statusText: string
  segments: MarqueeGaugeSegment[]
  /** Vertical position (%) of the current-value dot. */
  dotY: number
  /** Vertical positions (%) of the historical ghost dots. */
  ghosts: number[]
  muted?: boolean
}

const BAR_H = 79

function MarqueeCardView({
  card,
  className,
}: {
  card: MarqueeBiomarkerCard
  className?: string
}) {
  const muted = card.muted
  return (
    <div
      className={cn(
        "flex w-full flex-col gap-3 rounded-xl px-5 py-4 md:w-[calc(100%-144px)] md:flex-row md:items-end md:justify-between md:gap-4",
        muted
          ? "bg-neutral-100"
          : "bg-white shadow-[0_2px_6px_rgba(0,0,0,0.04),0_12px_32px_rgba(0,0,0,0.08)]",
        className
      )}
    >
      {/* Title spans full card width on mobile */}
      <p
        className={cn(
          "text-sm leading-tight font-normal md:hidden", // ds-typo-ignore: matches /report marquee
          muted ? "text-neutral-400" : "text-neutral-900"
        )}
      >
        {card.label}
      </p>
      <div className="flex items-end justify-between gap-4 md:contents">
        {/* Left: info */}
        <div className="flex min-w-0 flex-col">
          <p
            className={cn(
              "hidden text-sm leading-tight font-normal md:block", // ds-typo-ignore
              muted ? "text-neutral-400" : "text-neutral-900"
            )}
          >
            {card.label}
          </p>
          <div className="flex items-end gap-1.5 md:mt-1.5">
            <span
              className={cn(
                "type-headline-lg leading-none font-medium",
                muted ? "text-neutral-300" : "text-neutral-900"
              )}
            >
              {card.value}
            </span>
            <span
              className={cn(
                "type-body-sm pb-[1px] font-mono tracking-wider uppercase",
                muted ? "text-neutral-300" : "text-neutral-900"
              )}
            >
              {card.unit}
            </span>
          </div>
          <span
            className={cn(
              "type-label-sm mt-2 w-fit rounded-full px-2.5 py-0.5 font-medium whitespace-nowrap",
              muted
                ? "bg-neutral-200 text-neutral-500"
                : cn(card.statusBg, card.statusText)
            )}
          >
            {card.status}
          </span>
        </div>
        {/* Right: vertical range bar (matches app) */}
        <div
          className="relative flex-shrink-0"
          style={{ width: 143, height: BAR_H }}
        >
          {/* Active segment gradient highlight */}
          {!muted &&
            (() => {
              let yOffset = 0
              return card.segments.map((seg, si) => {
                const y = yOffset
                yOffset += (seg.h / 100) * BAR_H + 1
                return seg.active ? (
                  <div
                    key={si}
                    className="absolute right-0 left-0 rounded"
                    style={{
                      top: y,
                      height: (seg.h / 100) * BAR_H,
                      background: `linear-gradient(to right, transparent, ${seg.color}15)`,
                    }}
                  />
                ) : null
              })
            })()}
          {/* Vertical colored bar segments */}
          <svg
            className="absolute overflow-visible"
            style={{ right: 2, top: 0 }}
            width={5}
            height={BAR_H}
          >
            {(() => {
              let yOffset = 0
              return card.segments.map((seg, si) => {
                const y = yOffset
                const h = (seg.h / 100) * BAR_H
                yOffset += h + 1
                return (
                  <rect
                    key={si}
                    x={0}
                    y={y}
                    width={5}
                    height={h}
                    rx={2.5}
                    fill={muted ? "#e5e5e5" : seg.color}
                  />
                )
              })
            })()}
          </svg>
          {/* Dashed vertical line */}
          <div
            className="absolute border-l border-dashed border-gray-400 opacity-20"
            style={{ left: "75%", top: 0, bottom: 0 }}
          />
          {/* Ghost + current-value dots (hidden on the not-measured card) */}
          {!muted && (
            <>
              {card.ghosts.map((gy, gi) => (
                <div
                  key={gi}
                  className="absolute flex items-center justify-center rounded-full bg-gray-900 opacity-40"
                  style={{
                    width: 10,
                    height: 10,
                    left: `${65 - gi * 12}%`,
                    top: `${gy}%`,
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <div className="h-1 w-1 rounded-full bg-white" />
                </div>
              ))}
              <div
                className="absolute flex items-center justify-center rounded-full border-2 border-gray-900 bg-gray-900"
                style={{
                  width: 10,
                  height: 10,
                  left: "75%",
                  top: `${card.dotY}%`,
                  transform: "translate(-50%, -50%)",
                }}
              >
                <div className="h-1 w-1 rounded-full bg-white" />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Vertical auto-scrolling biomarker marquee — copied from the /report
 * "See your health in color, not chaos" section. The card list is duplicated
 * so the `biomarker-scroll` loop is seamless; hover pauses it; a mask fades the
 * top and bottom edges.
 */
export function BiomarkerReportMarquee({
  cards,
}: {
  cards: MarqueeBiomarkerCard[]
}) {
  return (
    <div
      className="relative overflow-hidden md:w-[62%]"
      style={{
        height: 420,
        maskImage:
          "linear-gradient(to bottom, transparent 0%, black 8%, black 92%, transparent 100%)",
        WebkitMaskImage:
          "linear-gradient(to bottom, transparent 0%, black 8%, black 92%, transparent 100%)",
      }}
    >
      {/* Seamless loop: the card list is rendered twice in ONE uniform-gap
          stack (gap-3 = 0.75rem between every card, including across the two
          copies), and we translate by exactly one copy's period. That period is
          `-50%` of the container MINUS half a gap, because -50% lands half a gap
          short of where copy 2 aligns onto copy 1. Using a single uniform gap
          (not a group-of-groups with a larger outer gap) is what keeps it from
          jumping back at the wrap. */}
      <style>{`
        @keyframes biomarker-scroll {
          0% { transform: translateY(0); }
          /* -50% lands the 2nd card set 12px (half the gap-6 inter-set gap)
             short of the 1st set's start, which reads as a jump on each loop.
             Subtracting that half-gap makes the wrap seamless. */
          100% { transform: translateY(calc(-50% - 12px)); }
        }
      `}</style>
      <div
        className="flex flex-col gap-3 px-6 hover:[animation-play-state:paused]"
        style={{ animation: "biomarker-scroll 50s linear infinite" }}
      >
        {[...cards, ...cards].map((card, i) => (
          <MarqueeCardView
            key={i}
            card={card}
            className={
              (i % cards.length) % 2 === 0 ? "md:self-start" : "md:self-end"
            }
          />
        ))}
      </div>
    </div>
  )
}
